import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from './firebase';

export const NotificationTypes = {
  CLAP: 'clap',
  TOMATO: 'tomato',
  FOLLOW: 'follow',
  MENTION: 'mention',
  BADGE: 'badge',
  HOT_TAKE_FEATURED: 'hot_take_featured',
  BOOKMARK: 'bookmark',
  MILESTONE: 'milestone'
};

export const createNotification = async (notification) => {
  try {
    const { userId, type, fromUser, fromUsername, target, message, metadata = {} } = notification;
    
    if (!userId || !type || !message) {
      throw new Error('Missing required notification fields');
    }

    const notificationData = {
      type,
      fromUser: fromUser || null,
      fromUsername: fromUsername || null,
      target: target || null,
      message,
      metadata,
      timestamp: serverTimestamp(),
      read: false,
      createdAt: Date.now()
    };

    const userNotificationsRef = collection(db, 'users', userId, 'notifications');
    const docRef = await addDoc(userNotificationsRef, notificationData);
    
    return {
      id: docRef.id,
      ...notificationData,
      timestamp: Date.now()
    };
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

export const getUserNotifications = async (userId, limitCount = 50) => {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toMillis() || doc.data().createdAt || Date.now()
    }));
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return [];
  }
};

export const getUnreadNotificationCount = async (userId) => {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(notificationsRef, where('read', '==', false));
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

export const markNotificationAsRead = async (userId, notificationId) => {
  try {
    const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
    await updateDoc(notificationRef, { read: true });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async (userId) => {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(notificationsRef, where('read', '==', false));
    
    const snapshot = await getDocs(q);
    const batch = [];
    
    snapshot.docs.forEach(doc => {
      batch.push(updateDoc(doc.ref, { read: true }));
    });
    
    await Promise.all(batch);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

export const subscribeToNotifications = (userId, callback) => {
  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      orderBy('timestamp', 'desc'),
      limit(50)
    );
    
    return onSnapshot(q, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toMillis() || doc.data().createdAt || Date.now()
      }));
      callback(notifications);
    });
  } catch (error) {
    console.error('Error subscribing to notifications:', error);
    return () => {};
  }
};

export const createClapNotification = async (targetUserId, fromUser, targetType, targetId, targetTitle) => {
  const message = `👏 ${fromUser.displayName || fromUser.username || 'Someone'} clapped your ${targetType}`;
  
  return createNotification({
    userId: targetUserId,
    type: NotificationTypes.CLAP,
    fromUser: fromUser.uid,
    fromUsername: fromUser.username || fromUser.displayName,
    target: targetId,
    message,
    metadata: {
      targetType,
      targetTitle: targetTitle?.substring(0, 50) + (targetTitle?.length > 50 ? '...' : '')
    }
  });
};

export const createTomatoNotification = async (targetUserId, fromUser, targetType, targetId, targetTitle) => {
  const message = `🍅 ${fromUser.displayName || fromUser.username || 'Someone'} threw a tomato at your ${targetType}`;
  
  return createNotification({
    userId: targetUserId,
    type: NotificationTypes.TOMATO,
    fromUser: fromUser.uid,
    fromUsername: fromUser.username || fromUser.displayName,
    target: targetId,
    message,
    metadata: {
      targetType,
      targetTitle: targetTitle?.substring(0, 50) + (targetTitle?.length > 50 ? '...' : '')
    }
  });
};

export const createFollowNotification = async (targetUserId, fromUser) => {
  const message = `👤 ${fromUser.displayName || fromUser.username || 'Someone'} started following you`;
  
  return createNotification({
    userId: targetUserId,
    type: NotificationTypes.FOLLOW,
    fromUser: fromUser.uid,
    fromUsername: fromUser.username || fromUser.displayName,
    message
  });
};

export const createMentionNotification = async (targetUserId, fromUser, targetType, targetId, targetTitle) => {
  const message = `💬 ${fromUser.displayName || fromUser.username || 'Someone'} mentioned you in a ${targetType}`;
  
  return createNotification({
    userId: targetUserId,
    type: NotificationTypes.MENTION,
    fromUser: fromUser.uid,
    fromUsername: fromUser.username || fromUser.displayName,
    target: targetId,
    message,
    metadata: {
      targetType,
      targetTitle: targetTitle?.substring(0, 50) + (targetTitle?.length > 50 ? '...' : '')
    }
  });
};

export const createBadgeNotification = async (userId, badgeName, badgeIcon) => {
  const message = `🎯 You unlocked the "${badgeName}" badge! ${badgeIcon}`;
  
  return createNotification({
    userId,
    type: NotificationTypes.BADGE,
    message,
    metadata: {
      badgeName,
      badgeIcon
    }
  });
};

export const createMilestoneNotification = async (userId, milestone, count) => {
  let message = '';
  
  switch (milestone) {
    case 'streak':
      message = `🔥 You hit a ${count}-day voting streak!`;
      break;
    case 'claps':
      message = `👏 You've received ${count} total claps!`;
      break;
    case 'followers':
      message = `📈 You now have ${count} followers!`;
      break;
    case 'votes':
      message = `🗳️ You've cast ${count} votes!`;
      break;
    default:
      message = `🎉 Milestone reached: ${milestone}!`;
  }
  
  return createNotification({
    userId,
    type: NotificationTypes.MILESTONE,
    message,
    metadata: {
      milestone,
      count
    }
  });
};

export const formatNotificationTime = (timestamp) => {
  const now = Date.now();
  const diff = now - timestamp;
  
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  
  return new Date(timestamp).toLocaleDateString();
};